import { ContactFormConfig } from '../types/ContactForm';
import { parseOptions, serializeOptions, addEmptyOption, removeOptionAtIndex, updateOptionAtIndex, cleanupEmptyOptions as cleanupOptions } from '../helpers/optionHelpers';

interface UseOptionOperationsProps {
    config: ContactFormConfig;
    updateField: (index: number, property: string, value: unknown) => void;
    setFocusedOption: (option: { fieldIndex: number, optionIndex: number } | null) => void;
    setJustCreatedOption: (option: { fieldIndex: number, optionIndex: number } | null) => void;
    justCreatedOption: { fieldIndex: number, optionIndex: number } | null;
}

export const useOptionOperations = ({ 
    config, 
    updateField, 
    setFocusedOption, 
    setJustCreatedOption,
    justCreatedOption 
}: UseOptionOperationsProps) => {
    const addOption = (fieldIndex: number): void => {
        const field = config.formFields[fieldIndex];
        const currentOptions = parseOptions(field.options).filter(opt => opt.trim() !== '');
        const newOptions = addEmptyOption(currentOptions);

        updateField(fieldIndex, 'options', serializeOptions(newOptions));

        const newOptionIndex = newOptions.length - 1;
        setFocusedOption({fieldIndex, optionIndex: newOptionIndex});
        setJustCreatedOption({fieldIndex, optionIndex: newOptionIndex});

        setTimeout(() => {
            setJustCreatedOption(null);
        }, 3000);
    };

    const removeOption = (fieldIndex: number, optionIndex: number): void => {
        const field = config.formFields[fieldIndex];
        const currentOptions = parseOptions(field.options);
        const nonEmptyOptions = currentOptions.filter(opt => opt.trim());

        if (nonEmptyOptions.length <= 1) return;

        const newOptions = removeOptionAtIndex(currentOptions, optionIndex);
        updateField(fieldIndex, 'options', serializeOptions(newOptions));
    };

    const updateOption = (fieldIndex: number, optionIndex: number, value: string): void => {
        const field = config.formFields[fieldIndex];
        const currentOptions = parseOptions(field.options);
        const newOptions = updateOptionAtIndex(currentOptions, optionIndex, value);
        
        updateField(fieldIndex, 'options', serializeOptions(newOptions));

        if (justCreatedOption &&
            justCreatedOption.fieldIndex === fieldIndex &&
            justCreatedOption.optionIndex === optionIndex &&
            value.trim() !== '') {
            setJustCreatedOption(null);
        }
    };

    const cleanupEmptyOptions = (fieldIndex: number): void => {
        const field = config.formFields[fieldIndex];

        if (justCreatedOption && justCreatedOption.fieldIndex === fieldIndex) {
            return;
        }

        const currentOptions = parseOptions(field.options);
        const cleanedOptions = cleanupOptions(currentOptions);
        updateField(fieldIndex, 'options', serializeOptions(cleanedOptions));
    };

    return {
        addOption,
        removeOption,
        updateOption,
        cleanupEmptyOptions
    };
};

